import isEmpty from 'lodash/isEmpty';
import { call, put, select, spawn } from 'redux-saga/effects';

import {
  fetchCustomEventMappings,
  fetchEventFieldMappings,
  fetchUserMappings,
} from 'api/WorkflowsAPI';
import { addNegativeToastWithErrorId } from 'modules/toastContainer';
import { ESTypeMap } from 'modules/workflows/types';
import { getErrorResponse } from 'utils/xhr';
import { actions } from '../actions';
import { getCustomEventMappings, getFieldMappings, getUserMappings } from '../selectors';
import { filterCustomEvents } from './sagaUtils/customEvents';

// Used for fetching all custom events and their mappings
function* fetchCustomEventMappingsSaga() {
  try {
    let customEventMappings: ReturnType<typeof getCustomEventMappings> =
      yield select(getCustomEventMappings);

    // only call the api if the custom events have not yet been loaded
    // otherwise, load from cache
    if (isEmpty(customEventMappings)) {
      const customEventResponse: ReturnType<typeof getCustomEventMappings> =
        yield call(fetchCustomEventMappings);
      customEventMappings = filterCustomEvents(customEventResponse || []);
    }
    yield put(actions.fetchCustomEventMappingsResponse(customEventMappings));
  } catch (error) {
    const err = getErrorResponse(error);
    yield put(actions.fetchCustomEventMappingsError(err));
    const message = 'Error fetching custom events. Please try again later.';
    yield put(addNegativeToastWithErrorId(message, 'fetchCustomEventMappingsSaga'));
  }
}

// Used for fetching all user events and their mappings
function* fetchUserMappingsSaga() {
  try {
    let userMappings: ReturnType<typeof getUserMappings> = yield select(getUserMappings);
    if (isEmpty(userMappings)) {
      userMappings = yield call(fetchUserMappings);
    }
    yield put(actions.fetchUserMappingsResponse(userMappings));
  } catch (error) {
    const err = getErrorResponse(error);
    yield put(actions.fetchUserMappingsError(err));
    const message = 'Error fetching user events. Please try again later.';
    yield put(addNegativeToastWithErrorId(message, 'fetchUserMappingsSaga'));
  }
}

export default function* fetchEventFieldMappingsSaga() {
  try {
    let fieldMappings: ReturnType<typeof getFieldMappings> = yield select(getFieldMappings);

    // only call the api if the field mappings have not yet been loaded
    // otherwise, load from cache
    if (isEmpty(fieldMappings)) {
      const mappingResponse: { mappings: ESTypeMap } = yield call(fetchEventFieldMappings);
      fieldMappings = mappingResponse?.mappings || {};
    }

    yield put(actions.fetchEventFieldMappingsResponse(fieldMappings));

    yield spawn(fetchCustomEventMappingsSaga);
    yield spawn(fetchUserMappingsSaga);
  } catch (error) {
    const err = getErrorResponse(error);
    yield put(actions.fetchEventFieldMappingsError(err));
    const message = `Error fetching fields. Please try again later.`;
    yield put(addNegativeToastWithErrorId(message, 'fetchEventFieldMappingsSaga'));
  }
}
